import { useEffect, useState } from "react";
import { Outlet, Link, useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  LayoutDashboard,
  Users,
  MessageSquare,
  Briefcase,
  LogOut,
  Menu,
  X,
  Truck,
  ExternalLink,
} from "lucide-react";
import { apiClient } from "@/lib/api";
import { cn } from "@/lib/utils";

declare const __COMMIT_HASH__: string;

interface AdminUser {
  id: number;
  email: string;
  name?: string;
  role: string;
}

export default function AdminLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState<AdminUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [features, setFeatures] = useState<Record<string, boolean>>({});

  useEffect(() => {
    checkAuth();
    loadFeatures();
  }, []);

  // Close sidebar on route change (e.g. browser back/forward)
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  const checkAuth = async () => {
    try {
      const response = await apiClient.admin.me();
      setUser(response.data.user as AdminUser);
    } catch {
      navigate("/admin/login?redirect=" + encodeURIComponent(location.pathname));
    } finally {
      setLoading(false);
    }
  };

  const loadFeatures = async () => {
    try {
      const response = await apiClient.admin.features();
      setFeatures(response.data as Record<string, boolean>);
    } catch {
      // Features endpoint may fail if not authenticated yet
    }
  };

  const handleLogout = async () => {
    try {
      await apiClient.admin.logout();
    } catch {
      // Ignore errors
    }
    navigate("/admin/login");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!user) {
    return null;
  }

  const navItems = [
    { href: "/admin", label: "Dashboard", icon: LayoutDashboard },
    ...(features.vacancyManagement
      ? [{ href: "/admin/vacancies", label: "Vacancies", icon: Briefcase }]
      : []),
    ...(features.applicationManagement
      ? [{ href: "/admin/applications", label: "Applications", icon: Users }]
      : []),
    ...(features.contactManagement
      ? [{ href: "/admin/contacts", label: "Contacts", icon: MessageSquare }]
      : []),
  ];

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Mobile header */}
      <div className="lg:hidden flex items-center justify-between p-4 border-b bg-background/95 backdrop-blur-sm">
        <div className="flex items-center gap-2">
          <Truck className="h-6 w-6 text-primary" />
          <span className="font-bold">RTC Admin</span>
        </div>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setSidebarOpen(!sidebarOpen)}
        >
          {sidebarOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </Button>
      </div>

      <div className="flex flex-1 lg:min-h-screen">
        {/* Sidebar */}
        <aside
          className={cn(
            "fixed top-[57px] bottom-0 left-0 z-50 w-64 bg-card/80 backdrop-blur-sm border-r transform transition-transform lg:top-0 lg:sticky lg:translate-x-0 lg:h-screen",
            sidebarOpen ? "translate-x-0" : "-translate-x-full"
          )}
        >
          <div className="flex flex-col h-full">
            {/* Logo */}
            <div className="hidden lg:flex items-center gap-2 p-6 border-b relative">
              <Truck className="h-6 w-6 text-primary" />
              <span className="font-bold tracking-tight">RTC Admin</span>
              <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-primary/50 via-primary/20 to-transparent" />
            </div>

            {/* Navigation */}
            <nav className="flex-1 p-4 space-y-1">
              {navItems.map((item) => {
                const isActive = location.pathname === item.href;
                return (
                  <Link
                    key={item.href}
                    to={item.href}
                    onClick={() => setSidebarOpen(false)}
                    className={cn(
                      "flex items-center gap-3 px-3 py-2 rounded-lg transition-all duration-200",
                      isActive
                        ? "bg-muted text-foreground font-medium border-l-2 border-primary"
                        : "text-muted-foreground hover:bg-muted/50 hover:text-foreground"
                    )}
                  >
                    <item.icon className={cn("h-5 w-5", isActive && "text-primary")} />
                    {item.label}
                  </Link>
                );
              })}
            </nav>

            {/* User section */}
            <div className="p-4 border-t relative space-y-2">
              <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary/20 to-transparent" />
              <Link
                to="/"
                target="_blank"
                className="flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground hover:text-foreground"
              >
                <ExternalLink className="h-4 w-4" />
                View Website
              </Link>
              <div className="px-3 py-2">
                <p className="text-sm font-medium">{user.name || user.email}</p>
                <p className="text-xs text-muted-foreground capitalize">
                  {user.role}
                </p>
              </div>
              <Button
                variant="ghost"
                className="w-full justify-start"
                onClick={handleLogout}
              >
                <LogOut className="h-4 w-4 mr-2" />
                Logout
              </Button>
              <p className="px-3 text-[10px] text-muted-foreground/50 font-mono">
                v{__COMMIT_HASH__}
              </p>
            </div>
          </div>
        </aside>

        {/* Overlay for mobile */}
        {sidebarOpen && (
          <div
            className="fixed inset-0 bg-black/50 z-40 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Main content */}
        <main className="flex-1 p-4 sm:p-6 lg:p-8 lg:overflow-y-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
